import React, { useContext, useEffect, useMemo, useState } from "react";
import { Form, message, Modal } from "antd";
import { UserContext, UserPayload } from "../../../../Context/userContext";
import { useAgentsStore } from "../../store/agents.store";
import { Content } from "./components/content/content.index";
import { Footer } from "./components/footer/footer.index";
import { CreateDownlineRequestFields } from "./createDownline.constants";
import { useCreateDownline } from "./createDownline.hooks";
import { ICreateDownlineRequest, SelectedProductCommission } from "./createDownline.models";
import styles from "./createDownlineModal.module.css";

interface CreateDownlineModalProps {
    open: boolean;
    onClose: () => void;
    selectedUserId: number | null;
    onSuccess?: () => void | Promise<void>;
}

export const CreateDownlineModal = ({
    open,
    onClose,
    selectedUserId,
    onSuccess,
}: CreateDownlineModalProps) => {
    const [form] = Form.useForm<Record<string, unknown>>();
    const { handleCreateDownline, createDownlineLoading } = useCreateDownline();
    const fetchAllAgents = useAgentsStore((state) => state.fetchAllAgents);
    const { setShowLoader } = useContext(UserContext) as UserPayload;
    const [pendingErrors, setPendingErrors] = useState<Array<{ name: string; errors: string[] }> | null>(
        null
    );

    useEffect(() => {
        if (!createDownlineLoading && pendingErrors) {
            form.setFields(pendingErrors);
            setPendingErrors(null);
        }
    }, [createDownlineLoading, pendingErrors, form]);

    const handleCancel = () => {
        if (createDownlineLoading) return;
        form.resetFields();
        onClose();
    };

    const handleSave = async () => {
        form.submit();
    };

    const handleSubmit = async (values: Record<string, string | number | SelectedProductCommission[]>) => {
        if (!selectedUserId) return;

        const {
            FIRST_NAME,
            LAST_NAME,
            PRIMARY_PHONE,
            CELL_PHONE,
            EMAIL,
            AGENCY_NAME,
            WEBSITE,
            USER_TYPE,
            PRODUCT_UID,
            COMMISSION_UID,
            WRITING_NUMBER,
            AGENCY_NUMBER,
            PARENT_USER_ID,
        } = CreateDownlineRequestFields;

        const payload: ICreateDownlineRequest = {
            [FIRST_NAME]: values[FIRST_NAME] as string,
            [LAST_NAME]: values[LAST_NAME] as string,
            [PRIMARY_PHONE]: values[PRIMARY_PHONE] as string,
            [CELL_PHONE]: values[CELL_PHONE] as string,
            [EMAIL]: values[EMAIL] as string,
            [AGENCY_NAME]: values[AGENCY_NAME] as string,
            [WEBSITE]: values[WEBSITE] as string,
            [USER_TYPE]: values[USER_TYPE] as number,
            productAndCommissionDetails: (values.products as SelectedProductCommission[]).map((product) => ({
                [PRODUCT_UID]: product.productUid,
                [COMMISSION_UID]: product.commissionUid,
            })),
            [WRITING_NUMBER]: values[WRITING_NUMBER] as string,
            [AGENCY_NUMBER]: values[AGENCY_NUMBER] as string,
            [PARENT_USER_ID]: selectedUserId,
        };

        const response = await handleCreateDownline(payload);
        if (response?.flagStatus) {
            message.success("Downline created successfully");
            handleCancel();
            await fetchAllAgents({ setShowLoader, force: true });
            await onSuccess?.();
        } else if (response?.validationErrors) {
            const errors = response.validationErrors as unknown as Record<string, string[]>;
            setPendingErrors(
                Object.entries(errors).map(([name, messages]) => ({ name, errors: messages }))
            );
        } else {
            message.error(response?.error ?? "Failed to create downline");
        }
    };

    const footer = useMemo(
        () => <Footer onCancel={handleCancel} onSave={handleSave} loading={createDownlineLoading} />,
        [createDownlineLoading]
    );

    return (
        <Modal
            open={open}
            onCancel={handleCancel}
            title=""
            footer={footer}
            centered
            width={600}
            zIndex={1200}
            closable={!createDownlineLoading}
            mask={{ closable: false }}
            keyboard={!createDownlineLoading}
            classNames={{
                container: styles.container,
                body: styles.body,
                footer: styles.footer,
                wrapper: styles.wrapper,
            }}
        >
            <Content
                form={form}
                onSubmit={handleSubmit}
                selectedUserId={selectedUserId}
                submitLoading={createDownlineLoading}
            />
        </Modal>
    );
};
